import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../lib/api';
import { storage, theme } from '../lib/utils';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('auth_token');
      const savedUser = storage.get('user');
      
      if (token && savedUser) {
        try {
          // Verify token is still valid
          const response = await authAPI.getMe();
          setUser(response.data);
          storage.set('user', response.data);
        } catch (err) {
          // Token invalid, clear storage
          localStorage.removeItem('auth_token');
          storage.remove('user');
        }
      }
      
      // Initialize theme
      theme.init();
      setLoading(false);
    };
    
    initAuth();
  }, []);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const response = await authAPI.login({ email, password });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('auth_token', access_token);
      storage.set('user', userData);
      setUser(userData);
      
      return userData;
    } catch (err) {
      const message = err.response?.data?.detail || 'Error al iniciar sesión';
      setError(message);
      throw new Error(message);
    }
  }, []);

  const register = useCallback(async (data) => {
    setError(null);
    try {
      const response = await authAPI.register(data);
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('auth_token', access_token);
      storage.set('user', userData);
      setUser(userData);
      
      return userData;
    } catch (err) {
      const message = err.response?.data?.detail || 'Error al registrarse';
      setError(message);
      throw new Error(message);
    }
  }, []);

  const socialLogin = useCallback(async (provider, providerData) => {
    setError(null);
    try {
      const response = await authAPI.socialLogin({
        provider,
        token: providerData.token || 'mock-token',
        email: providerData.email,
        full_name: providerData.name,
        avatar_url: providerData.avatar,
      });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('auth_token', access_token);
      storage.set('user', userData);
      setUser(userData);
      
      return userData;
    } catch (err) {
      const message = err.response?.data?.detail || 'Error con login social';
      setError(message);
      throw new Error(message);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('auth_token');
    storage.remove('user');
    setUser(null);
    setError(null);
  }, []);

  const updateUser = useCallback((updates) => {
    const updated = { ...user, ...updates };
    storage.set('user', updated);
    setUser(updated);
  }, [user]);

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    isPlatformAdmin: user?.role === 'platform_admin',
    isStoreAdmin: user?.role === 'store_admin' || user?.role === 'platform_admin',
    isStoreStaff: ['store_staff', 'store_admin', 'platform_admin'].includes(user?.role),
    isCustomer: user?.role === 'customer',
    login,
    register,
    socialLogin,
    logout,
    updateUser,
    clearError: () => setError(null),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
